<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.sellingPriceEdit')"
        size="large"
        :rules="CRules"
        :submit="handleSubmit"
        @close="closeOverSide">
        <div :style="{ minWidth: minWidthScroll }">
            <hts-card :title="$t('labels.fleetVehicle')">
                <div class="grid gap-x-2 grid-cols-3">
                    <hts-select-cooperator-contact
                        v-model="dataEdit.contact"
                        v-model:name="dataEdit.contactName"
                        :disabled="!dataEdit.vendor"
                        :cooperator-id="dataEdit.vendor"
                        :label="$t('labels.vendorPriceModule.contact')"
                        :creatable="true"
                        required
                        prop="contact"
                        name-select="vendorPriceModule.contact"
                        :param-prepare="dataEdit.vendor"
                        @change="(contact) => selectContact(contact)" />
                    <hts-form-input
                        v-model="dataEdit.phone"
                        :label="$t('labels.phone')"
                        disabled />
                    <hts-form-input
                        v-model="dataEdit.email"
                        :label="$t('labels.email')"
                        disabled />
                </div>
            </hts-card>

            <hts-card :title="$t('labels.vendorPriceModule.priceInfo')">
                <div class="w-full">
                    <hts-select-work-flow
                        v-model="dataEdit.workflow"
                        v-model:name="dataEdit.workflowName"
                        prop="workflow"
                        :disabled="disabled"
                        @change="selectWorkflow" />
                    <hts-workflow-position
                        :positions="dataEdit.positions"
                        :same-positions="dataEdit.theSamePositions"
                        :is-active="true">
                        <template #default="{ item, index }">
                            <span>
                                {{ item.nodeTypeName }}
                            </span>
                            <hts-form-select
                                v-model="dataEdit.positions[index].quoteEqual"
                                required
                                class="mr-2"
                                :label="$t('labels.quoteEqual')"
                                :items="listSelectQuoteEqual"
                                :prop="'quoteEquals' + index"
                                @change="handleChangeSelectedQuoteEqual(index)" />
                        </template>
                    </hts-workflow-position>
                    <div
                        v-if="dataEdit.positions.length > 0"
                        v-loading="loadingSelectNode">
                        <span class="text-base block px-0 font-medium text-default-500">
                            {{ $t('labels.route') }}
                        </span>
                        <div
                            v-for="(item, index) in dataEdit.positions"
                            :key="index"
                            class="pl-10">
                            <hts-select-node
                                v-model="dataEdit.positions[index].node"
                                v-model:name="dataEdit.positions[index].fullNameNode"
                                :node-type-id="item.nodeType"
                                :prop="item.nodeType + index"
                                :label="item.nodeTypeName"
                                :businesses="item.businesses"
                                :disabled="disabled"
                                required
                                :location-type="item.quoteEqual"
                                :key-label="getKeyLabel(item.quoteEqual)"
                                :key-sub-label="getKeySubLabel(item.quoteEqual)"
                                :param-prepare="`${dataEdit.positions[index].quoteEqual}-${dataEdit.positions[index].nodeType}`"
                                @change="(val) => handelChangeSelectNode(index, val)" />
                        </div>

                        <div v-if="dataEdit.transportation">
                            <div v-if="dataEdit.transportation === 'CONTAINER'">
                                <hts-form-select
                                    v-model="dataEdit.contCategory"
                                    :label="$t('labels.containerType')"
                                    :clearable="true"
                                    :required="true"
                                    :items="dataSelectWorkFlowData"
                                    key-id="value"
                                    key-label="label"
                                    prop="workflowData"
                                    name="workflowData"
                                    :disabled="disabled"
                                    :color="props.color" />
                                <div class="flex">
                                    <hts-form-checkbox
                                        v-if="dataEdit.contCategory === 20"
                                        v-model="dataEdit.mergeContainer"
                                        :disabled="disabled"
                                        color="secondary"
                                        :items="[{ label: $t('labels.mergeContainer'), value: false }]" />
                                    <hts-form-checkbox
                                        v-model="dataEdit.headContainer"
                                        :disabled="disabled"
                                        color="secondary"
                                        :items="[{ label: $t('labels.headContainer'), value: false }]" />
                                </div>
                            </div>
                            <div v-else>
                                <hts-select-category-vehicle
                                    v-model="dataEdit.vehicleCategory"
                                    v-model:name="dataEdit.vehicleCategoryName"
                                    required
                                    :disabled="disabled"
                                    prop="workflowData" />
                            </div>
                        </div>
                        <div class="block mt-5">
                            <div class="inline-block align-middle">
                                <span>
                                    {{ $t('labels.maxTaskCompletionTime') }}
                                </span>
                                <span class="text-error-500">
                                    *
                                </span>
                            </div>

                            <div
                                class="tooltip inline-block align-middle"
                                :data-tip="$t('labels.warningTaskQuoteRequest')">
                                <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                            </div>
                        </div>

                        <div class="block mb-5 ml-8">
                            <div
                                v-for="(item, index) in dataEdit.tasks"
                                :key="index"
                                class="grid grid-cols-2 gap-4">
                                <hts-form-select
                                    v-model="dataEdit.tasks[index].code"
                                    required
                                    :items="taskItems"
                                    key-id="code"
                                    key-label="taskName"
                                    key-sub-label="businessName"
                                    key-disabled="disabled"
                                    name="task"
                                    :prop="'task' + index"
                                    @change="(val) => handleChangeSelectedTask(val, index)" />
                                <div class="flex justify-center items-center">
                                    <hts-form-input
                                        v-model="dataEdit.tasks[index].value"
                                        type="number"
                                        :items="taskItems"
                                        name="completionTime"
                                        class="flex-1"
                                        :prop="'time' + index">
                                        <template #suffix>
                                            <span class="capitalize">
                                                {{ $t('labels.hour') }}
                                            </span>
                                        </template>
                                    </hts-form-input>
                                    <hi-x-circle-icon
                                        v-if="dataEdit.tasks.length > 1"
                                        class="h-6 w-6 mb-3 ml-2 text-error-600 cursor-pointer"
                                        @click="deleteTask(index)" />
                                </div>
                            </div>
                            <div class="flex mt-2 text-main-600">
                                <div
                                    class="flex cursor-pointer"
                                    @click="addTask">
                                    <hi-plus-circle-icon class="h-5 w-5" />
                                    <span>
                                        {{ $t('labels.add1') }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="grid gap-x-2 grid-cols-2">
                    <hts-form-date
                        v-model="dataEdit.effectiveFromDate"
                        :label="$t('labels.vendorPriceModule.effectiveFromDate')"
                        type="date"
                        required
                        prop="vendorPriceModule.effectiveFromDate"
                        name="vendorPriceModule.effectiveFromDate" />
                    <hts-form-date
                        v-model="dataEdit.effectiveToDate"
                        :label="$t('labels.effectiveToDateDescription')"
                        type="date"
                        prop="vendorPriceModule.effectiveToDate"
                        name="vendorPriceModule.effectiveToDate" />
                    <hts-form-select
                        v-model="dataEdit.typeCommodity"
                        :label="$t('labels.goodsTypes')"
                        required
                        :items="cTypeCommodity || []"
                        multiple
                        select-all-disabled
                        key-id="key"
                        key-label="title"
                        :color="color"
                        :disabled="disabled"
                        name="goodsTypes"
                        prop="commodityType" />
                    <hts-form-input
                        v-model="dataEdit.weightCommodity"
                        :label="$t('labels.weightCommodityByKGMaximum')"
                        type="number"
                        required
                        prop="weightCommodityByKGMaximum"
                        name="weightCommodityByKGMaximum" />
                </div>
                <div>
                    <hts-form-textarea
                        v-model="dataEdit.description"
                        :label="$t('labels.description')" />
                </div>
            </hts-card>
            <hts-card :title="$t('labels.priceInfo')">
                <div class="grid gap-x-2 grid-cols-2">
                    <hts-form-input
                        v-model="dataEdit.price"
                        label="Giá bán"
                        required
                        type="number" />
                    <hts-form-input
                        v-model="dataEdit.oilPrice"
                        :label="$t('labels.oilPriceNotVat')"
                        type="number" />
                </div>
            </hts-card>
        </div>
    </hts-drawer>
</template>

<script lang="ts" setup>
import { getTypeCommodity, IVendorPrice } from '../ignore/data'
import { ruleAddVendorPrices } from '../ignore/form'

import screen from '~/components/utils/screen'
const { map: mapStore } = $store()

const { $disableReactive } = useNuxtApp()
const store = $store()

const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm + 100}px`
})

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 2
  }
})

const emits = defineEmits(['refreshTables'])

const visible = ref(false)
const priceVendorId = ref('')

const minWeightCommodity = ref(-1)
const priceOfflineFee = ref<any[]>([])
const passCreateFromChange = ref(false)
const priceId = ref('')

const open = (id: string) => {
  priceVendorId.value = id

  getData()
}

const getData = () => {
  mapStore.setShowLocation({ priceVendor: false })

  visible.value = true
  passCreateFromChange.value = true
  if (priceVendorId.value) {
    const api = {
      url: '/pp/selling-price/ID',
      method: 'get'
    }

    $api(api, { uriParams: { ID: priceVendorId.value || '' } }).then((data) => {
      const resData: any = data?.data?.value
      if (resData.success) {
        Object.assign(dataEdit.value, { ...resData.data })
        if (dataEdit.value.vendor && typeof dataEdit.value.vendor !== 'string') {
          dataEdit.value.vendorName = dataEdit.value.vendor.name
          dataEdit.value.vendor = dataEdit.value.vendor._id
        }

        if (dataEdit.value.contact && typeof dataEdit.value.contact !== 'string') {
          dataEdit.value.email = dataEdit.value.contact?.email
          dataEdit.value.phone = dataEdit.value.contact?.phone
          dataEdit.value.contactName = dataEdit.value.contact?.fullName
          dataEdit.value.contact = dataEdit.value.contact?._id
        }
        dataWorkflow.value.positions = resData.data.positions

        minWeightCommodity.value = dataEdit.value.minWeightCommodity || -1

        priceId.value = resData.data._id

        if (resData.data.createPriceFrom === 2) {
          quoteRequestChange([resData.data.quoteRequestData], false)
        }
        nextTick(() => {
          passCreateFromChange.value = false
        })
      }
    })
  }
}

const init: IVendorPrice = {
  createPriceFrom: 1,
  quote: null,
  quoteCode: null,
  quoteRequest: null,
  quoteRequestCode: null,
  vendor: null,
  contact: null,
  contactName: '',
  vendorName: '',
  email: null,
  phone: null,
  workflow: null,
  workflowName: '',
  transportation: '',
  contCategory: '',
  vehicleCategory: '',
  effectiveFromDate: null,
  effectiveToDate: null,
  price: null,
  oilPrice: null,
  description: null,
  mergeContainer: false,
  headContainer: false,
  positions: [],
  theSamePositions: [],
  typeCommodity: [],
  vehicleCategoryName: null,
  weightCommodity: null,
  tasks: [
    {
      code: '',
      value: ''
    }
  ],
  priceOfflineFee: []
}

const dataEdit = ref<IVendorPrice>({
  createPriceFrom: 1,
  quote: null,
  quoteCode: null,
  quoteRequest: null,
  quoteRequestCode: null,
  vendor: null,
  contact: null,
  contactName: '',
  vendorName: '',
  email: null,
  phone: null,
  workflow: null,
  workflowName: '',
  transportation: '',
  contCategory: '',
  vehicleCategory: '',
  effectiveFromDate: null,
  effectiveToDate: null,
  price: null,
  oilPrice: null,
  description: null,
  mergeContainer: false,
  headContainer: false,
  positions: [],
  theSamePositions: [],
  typeCommodity: [],
  vehicleCategoryName: null,
  weightCommodity: null,
  tasks: [
    {
      code: '',
      value: ''
    }
  ],
  priceOfflineFee: []
})
const popover = ref()

const selectContact = (contact: any) => {
  if (Array.isArray(contact) && contact.length) {
    dataEdit.value.contactName = contact[0]?.fullName || ''
    dataEdit.value.email = contact[0]?.email || ''
    dataEdit.value.phone = contact[0]?.phone || ''
    return
  }

  dataEdit.value.contactName = contact?.fullName || ''
  dataEdit.value.email = contact?.email || ''
  dataEdit.value.phone = contact?.phone || ''
}

const dataWorkflow = ref<{ [key: string]: any }>([])
const selectWorkflow = (workflow: any) => {
  if (Array.isArray(workflow) && workflow.length) {
    dataWorkflow.value = workflow[0]
  } else {
    dataWorkflow.value = []
  }
  setDataHandleSelectWorkflow()
}

const setDataHandleSelectWorkflow = () => {
  dataEdit.value.positions = dataWorkflow.value.positions || []
  dataEdit.value.transportation = dataWorkflow.value.transportation || ''
  dataEdit.value.theSamePositions = dataWorkflow.value.theSamePositions || ''
  dataEdit.value.vehicleCategory = null
  dataEdit.value.vehicleCategoryName = ''
  dataEdit.value.contCategory = null
  dataEdit.value.tasks = [
    {
      code: '',
      value: ''
    }
  ]
  setDataPosition()
}
const { $formatters } = useNuxtApp()

const setDataPosition = () => {
  dataEdit.value.positions.forEach((position: { [key: string]: any }, index: number) => {
    if (['Cảng', 'DEPOT', 'ICD'].includes(position.nodeTypeName)) {
      dataEdit.value.positions[index].quoteEqual = 'NODE'
    } else {
      dataEdit.value.positions[index].quoteEqual = 'WARD'
    }
  })
}

const handleSubmit = async () => {
  const api = {
    url: '/pp/selling-price/ID',
    method: 'put'
  }
  const data: any = await $api(api, {
    uriParams: { ID: priceId.value || '' },
    body: {
      ...dataEdit.value,
      minWeightCommodity: minWeightCommodity.value
    }
  })
  const resData: any = data?.data?.value

  if (resData && resData.success) {
    process.client && $toast().success($t('labels.updateSuccess'))
    emits('refreshTables')
    closeOverSide()
  }
}

const taskData: { [key: string]: any } = computed(() => {
  return store.data.getTasks
})

const configCont: { [key: string]: any } = computed(() => store.globalSettings.getGlobalSettings.configCont)
const configVehicle: { [key: string]: any } = computed(() => {
  return store.data.getVehicleCategories
})

const CRules = computed(() => {
  const rules = { ...ruleAddVendorPrices } as { [key: string]: string }
  if (dataEdit.value.effectiveFromDate) {
    const date = dataEdit.value.effectiveFromDate ? new Date(dataEdit.value.effectiveFromDate) : ''
    if (date && dataEdit.value.effectiveToDate) {
      date.setDate(date.getDate() + 1)
      rules['vendorPriceModule.effectiveToDate'] = rules['vendorPriceModule.effectiveToDate'] + `|min_date:${date.toISOString().split('T')[0]}`
    }
  }
  dataEdit.value.tasks.forEach((task, index) => {
    const dataTask: { [key: string]: any } = taskData.value.find((iTask: { [key: string]: any }) => iTask._id === task.task)
    if (dataTask && dataTask.completionTimes) {
      const keyCompletionTime = dataEdit.value.transportation === 'CONTAINER' ? 'contCategory' : 'vehicleCategory'
      const minimumTime =
        dataTask.completionTimes &&
        dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''] &&
        dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''].minimumTime
          ? dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''].minimumTime
          : 0
      if (dataEdit.value.createPriceFrom === 3) {
        rules['time' + index] = `min_value:${minimumTime}`
      } else {
        rules['time' + index] = `validationCustom:
        ${task.minimumTime <= task.value}--
        Thời gian hoàn thành tối đa của tác vụ phải lớn hơn hoặc bằng thời gian hoàn thành được yêu cầu từ yêu cầu báo giá (${task.minimumTime} giờ)`
      }
    }
  })
  if (dataEdit.value.transportation === 'CONTAINER' && dataEdit.value.contCategory) {
    if (configCont.value && configCont.value[dataEdit.value.contCategory] && configCont.value[dataEdit.value.contCategory].weightCargo) {
      const formatWeightCargo = $formatters.formatCurrency(configCont.value[dataEdit.value.contCategory].weightCargo).replaceAll(',', 'LA==')
      if (minWeightCommodity.value < 0 && (dataEdit.value.weightCommodity || 0) >= configCont.value[dataEdit.value.contCategory].weightCargo) {
        rules.weightCommodityByKGMaximum = `required|validationCustom:
        ${dataEdit.value.weightCommodity && dataEdit.value.weightCommodity <= configCont.value[dataEdit.value.contCategory].weightCargo}--
        ${$t('labels.checkWeightContainer')} - ${configCont.value[dataEdit.value.contCategory].weightCargo}`
      } else {
        rules.weightCommodityByKGMaximum = `required|validationCustom:
        ${
          dataEdit.value.weightCommodity &&
          dataEdit.value.weightCommodity >= minWeightCommodity.value &&
          dataEdit.value.weightCommodity <= configCont.value[dataEdit.value.contCategory].weightCargo
        }--
        ${$t('labels.checkWeightVehicle2')} (${minWeightCommodity.value} kg)
        và nhỏ hơn trọng lượng hàng tối đa được cài đặt cho loại contaier ${listLabelCont[Number(dataEdit.value.contCategory)]} - ${formatWeightCargo} kg.`
      }
    }
  } else if (dataEdit.value.transportation === 'NON_CONTAINER' && dataEdit.value.vehicleCategory && configVehicle.value) {
    const findVehicleCategory = configVehicle.value.find((findVehicleCategory: { [key: string]: any }) => {
      return findVehicleCategory._id === dataEdit.value.vehicleCategory
    })
    if (findVehicleCategory) {
      const min = Number(findVehicleCategory.from || 0)
      const max = Number(findVehicleCategory.to || 999999999999999)
      const formatMax = $formatters.formatCurrency(max).replaceAll(',', 'LA==')
      if (minWeightCommodity.value < 0 && (dataEdit.value.weightCommodity || 0) >= max) {
        rules.weightCommodityByKGMaximum = `required|validationCustom:
        ${dataEdit.value.weightCommodity && min <= dataEdit.value.weightCommodity && dataEdit.value.weightCommodity < max}--
        ${$t('labels.checkWeightVehicle')} (${min} <= ${$t('labels.weightCommodity')} < ${max})`
      } else {
        rules.weightCommodityByKGMaximum =
          `required|validationCustom:
        ${dataEdit.value.weightCommodity && dataEdit.value.weightCommodity >= minWeightCommodity.value && dataEdit.value.weightCommodity < max}--
        ${$t('labels.checkWeightVehicle2')} (${minWeightCommodity.value} kg) và nhỏ hơn trọng tải tối đa của loại xe` +
          ` ${findVehicleCategory.name || ''} - ${formatMax} kg`
      }
    }
  }
  return rules
})

const loadingSelectNode = ref(false)

const handelChangeSelectNode = async (indexRoute: number, val: Array<{ [key: string]: any }>) => {
  loadingSelectNode.value = true
  const nodeTheSame: Array<number> | undefined = getNodeTheSamePosition(indexRoute, $disableReactive(dataEdit.value.theSamePositions))
  if (Array.isArray(val) && val.length && typeof val[0].location === 'string') {
    let dataPosition: { [key: string]: any } = {}
    const api = $urlAdmin.node.location.detail
    const res = await $api(api, { uriParams: { ID: val[0].location } })
    const resData: any = res?.data?.value
    if (resData?.success) {
      dataPosition = resData.data
    }
    if (dataPosition) {
      dataEdit.value.positions[indexRoute].lat = val[0]?.locationData?.[0]?.lat || dataPosition.lat
      dataEdit.value.positions[indexRoute].lng = val[0]?.locationData?.[0]?.lng || dataPosition.lng
      if (Array.isArray(nodeTheSame)) {
        nodeTheSame.forEach((numberRoute: number) => {
          if (numberRoute !== indexRoute) {
            if (Array.isArray(dataPosition.nodeDetail) && dataPosition.nodeDetail.length) {
              let findItemPosition = dataPosition.nodeDetail.find((itemNode: { [key: string]: any }) => {
                return itemNode.nodeType && itemNode.nodeType.name === dataEdit.value.positions[numberRoute].nodeTypeName
              })
              const businessAndTask = checkBusinessAndTask(findItemPosition, dataEdit.value.positions[numberRoute])
              if (businessAndTask) {
                findItemPosition = undefined
              }
              dataEdit.value.positions[numberRoute].node = findItemPosition?._id || ''
              dataEdit.value.positions[numberRoute].fullNameNode = findItemPosition?.fullNameNode || ''
              dataEdit.value.positions[numberRoute].lat = val[0]?.locationData?.[0]?.lat || dataPosition.lat
              dataEdit.value.positions[numberRoute].lng = val[0]?.locationData?.[0]?.lng || dataPosition.lng
            }
          }
        })
      }
    }
  } else {
    dataEdit.value.positions[indexRoute].lat = ''
    dataEdit.value.positions[indexRoute].lng = ''
    nodeTheSame &&
      nodeTheSame.forEach((numberRoute: number) => {
        if (numberRoute !== indexRoute) {
          dataEdit.value.positions[numberRoute].node = ''
          dataEdit.value.positions[numberRoute].fullNameNode = ''
          dataEdit.value.positions[numberRoute].lat = ''
          dataEdit.value.positions[numberRoute].lng = ''
        }
      })
  }
  loadingSelectNode.value = false
}

const listSelectQuoteEqual = [
  {
    value: 'NODE',
    label: $t('labels.node')
  },
  {
    value: 'WARD',
    label: $t('labels.ward')
  },
  {
    value: 'DISTRICT',
    label: $t('labels.district')
  },
  {
    value: 'CITY',
    label: $t('labels.city')
  }
]

const dataSelectWorkFlowData = [
  {
    value: 20,
    label: $t('labels.vehicleModule.20ft')
  },
  {
    value: 40,
    label: $t('labels.vehicleModule.40ft')
  },
  {
    value: 45,
    label: $t('labels.vehicleModule.45ft')
  }
]

const handleChangeSelectedTask = (data: any, index: number) => {
  if (Array.isArray(data) && data.length) {
    if (dataEdit.value.createPriceFrom === 2 || dataEdit.value.createPriceFrom === 1) {
      const indexData = dataEdit.value.tasks.findIndex((task: { [key: string]: any }) => {
        return task.code === data[0].code
      })
      if (indexData !== -1) {
        dataEdit.value.tasks[indexData] = { ...dataEdit.value.tasks[indexData], ...data[0] }
        const dataPosition = dataWorkflow.value.positions.find((position: { [key: string]: any }) => {
          return data[0]._id === position._id && data[0].nodeType === position.nodeType && data[0].node === position.node
        })
        if (dataPosition) {
          const dataTask = dataPosition.tasks.find((task: { [key: string]: any }) => {
            return task.business === data[0].business && task.task === data[0].task
          })
          if (dataTask) {
            dataEdit.value.tasks[indexData].value = dataTask.timeComplete
            dataEdit.value.tasks[indexData].minimumTime = dataTask.timeComplete
          }
        }
      }
    } else {
      const indexData = dataEdit.value.tasks.findIndex((task: { [key: string]: any }) => {
        return task.code === data[0].code
      })
      if (indexData !== -1) {
        dataEdit.value.tasks[indexData] = { ...dataEdit.value.tasks[indexData], ...data[0] }
        const dataTask: { [key: string]: any } = taskData.value.find((iTask: { [key: string]: any }) => iTask._id === dataEdit.value.tasks[indexData].task)
        if (dataTask && dataTask.completionTimes) {
          const keyCompletionTime = dataEdit.value.transportation === 'CONTAINER' ? 'contCategory' : 'vehicleCategory'
          const value =
            dataTask.completionTimes &&
            dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''] &&
            dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''].averageTime
              ? dataTask.completionTimes[keyCompletionTime][dataEdit.value[keyCompletionTime] || ''].averageTime
              : ''
          dataEdit.value.tasks[indexData].value = value
          dataEdit.value.tasks[indexData].minimumTime = value
        }
      }
    }
  } else {
    dataEdit.value.tasks[index].value = ''
    dataEdit.value.tasks[index].minimumTime = ''
  }
}

const taskItems = computed(() => {
  const result: Array<{ [key: string]: any }> = []
  if (Array.isArray(dataEdit.value.positions)) {
    dataEdit.value.positions.forEach((positions: { [key: string]: any }) => {
      if (Array.isArray(positions.tasks)) {
        positions.tasks.forEach((task: { [key: string]: any }) => {
          const index = result.findIndex((i) => i.key === task.key)
          if (index === -1 && task.responsibleForImplementation === 'CUSTOMER') {
            task.nodeType = positions.nodeType || ''
            task.node = positions.node || ''
            task.index = positions.index || ''
            task._id = positions._id || ''
            task.nodeTypeName = positions.nodeTypeName || ''
            task.code = (positions.nodeType || '') + '-' + task.key
            const findIndexSelected = dataEdit.value.tasks.findIndex((iTask) => iTask.code === task.code)
            task.disabled = false
            if (findIndexSelected !== -1) {
              task.disabled = true
            }
            result.push(task)
          }
        })
      }
    })
  }
  return result
})

const getNodeTheSamePosition = (index: number, data: Array<Array<number>>) => {
  if (typeof index !== 'number' || !Array.isArray(data)) {
    return
  }
  let result: Array<number> = []
  const filteredItems = data.filter((i: Array<number>) => i.includes(index))
  const tempData = data.filter((i: Array<number>) => !i.includes(index))
  filteredItems.forEach((item) => {
    if (!Array.isArray(item) || item.length !== 2) {
      return
    }
    const tempIndex = item.findIndex((o) => o === index)
    let tempIndex2
    if (tempIndex === 0) {
      tempIndex2 = 1
    } else {
      tempIndex2 = 0
    }

    result.push(item[1])
    result.push(item[0])

    const itemNodeTheSames: Array<number> | undefined = getNodeTheSamePosition(item[tempIndex2], tempData)
    if (Array.isArray(itemNodeTheSames)) {
      result = result.concat(itemNodeTheSames)
    }
  })
  return $lodash.uniq(result)
}
const mapPosition = ref()

const openWithMap = () => {
  mapPosition.value && mapPosition.value.open($disableReactive(dataEdit.value.positions), $disableReactive(dataEdit.value.theSamePositions))
}

const addTask = () => {
  dataEdit.value.tasks.push({
    code: '',
    value: null
  })
}
const handleApplyPositionMap = (positions: Array<{ [key: string]: any }>) => {
  dataEdit.value.positions = $disableReactive(positions)
}

const cTypeCommodity = computed(() => {
  const result: Array<{ [key: string]: any }> = $disableReactive(typeCommodity)
  if (dataEdit.value.typeCommodity && dataEdit.value.typeCommodity.length === 1 && dataEdit.value.typeCommodity.includes('NORMAL')) {
    result.forEach((i: { [key: string]: any }, index: number) => {
      if (i.key !== 'NORMAL') {
        result[index].disabled = true
      }
    })
  } else if (dataEdit.value.typeCommodity && dataEdit.value.typeCommodity.length && !dataEdit.value.typeCommodity.includes('NORMAL')) {
    result[0].disabled = true
  }
  return result
})

const typeCommodity = [
  {
    key: 'NORMAL',
    title: $t('labels.ordinaryGoods')
  },
  // {
  //   key: 'LIQUID',
  //   title: $t('labels.liquid')
  // },
  // {
  //   key: 'GAS',
  //   title: $t('labels.gas')
  // }, ko xoá cmt
  {
    key: 'FROZEN',
    title: $t('labels.frozen')
  },
  {
    key: 'DANGEROUS',
    title: $t('labels.dangerousGoods')
  },
  {
    key: 'FRAGILE',
    title: $t('labels.fragileGoods')
  }
  // {
  //   key: 'OVERWEIGHT',
  //   title: $t('labels.overweightCargo')
  // } ko xoá cmt
]

const getKeySubLabel = (locationType: string) => {
  if (locationType === 'NODE') {
    return 'address'
  }
  return 'address'
}
const getKeyLabel = (locationType: string) => {
  if (locationType === 'NODE') {
    return 'name'
  }
  return 'addressByType'
}

const handleChangeSelectedQuoteEqual = (index: number) => {
  dataEdit.value.priceOfflineFee = []
  const position = dataEdit.value.positions[index]

  if (dataEdit.value.createPriceFrom === 3) {
    position.node = ''
    position.fullNameNode = ''
    return
  }

  let keyAddress = ''

  switch (position.quoteEqual) {
  case 'NODE':
    keyAddress = 'address'
    break
  case 'WARD':
    keyAddress = 'areaLevel3'
    break
  case 'DISTRICT':
    keyAddress = 'areaLevel2'
    break
  case 'CITY':
    keyAddress = 'areaLevel1'
    break
  }

  if (!keyAddress) {
    dataEdit.value.positions[index].node = ''
    dataEdit.value.positions[index].fullNameNode = ''
    return
  }

  const nodeData = position.nodeData
  const fullNameNode = keyAddress === 'address' ? (position.fullNameNode = position.nodeName) : nodeData.addressDetail?.[keyAddress]?.formatted_address || ''

  if (!fullNameNode) {
    dataEdit.value.positions[index].node = ''
    dataEdit.value.positions[index].fullNameNode = ''
    return
  }

  dataEdit.value.positions[index].fullNameNode = fullNameNode
}
const deleteTask = (index: number) => {
  dataEdit.value.tasks.splice(index, 1)
}

const refreshTables = () => {
  emits('refreshTables')
}

const checkBusinessAndTask = (node: { businesses: { [key: string]: Array<string> } }, route: { [key: string]: any }): boolean => {
  if (!route.businesses || !Object.keys(route.businesses).length) {
    return false
  }
  return Object.keys(route.businesses).some((item: string) => {
    if (!node || !node.businesses || !Object.keys(node.businesses).length || !node?.businesses[item]) {
      return true
    }
    return route.businesses[item].some((task: string) => {
      return !node?.businesses[item].includes(task)
    })
  })
}

const resetField = () => {
  dataEdit.value = $disableReactive(init)
  minWeightCommodity.value = -1
  priceOfflineFee.value = []
  dataWorkflow.value.positions = []
}

const createPriceFromChange = (value: number) => {
  if (passCreateFromChange.value) {
    return
  }
  resetField()
  dataEdit.value.createPriceFrom = value
}

const quoteChange = (values: any) => {
  const quoteData = values?.[0] || ({} as any)
  dataEdit.value.workflow = quoteData.workflow || ''
  dataEdit.value.workflowName = quoteData.workflowName || ''
  dataEdit.value.positions = quoteData.positions || []
  dataEdit.value.transportation = quoteData.transportation || ''
  dataEdit.value.mergeContainer = quoteData.mergeContainer || false
  dataEdit.value.headContainer = quoteData.headContainer || false
  dataEdit.value.vehicleCategory = quoteData.vehicleCategory || ''
  dataEdit.value.vehicleCategoryName = quoteData.vehicleCategoryName || ''
  dataEdit.value.contCategory = quoteData.contCategory || null
  dataEdit.value.typeCommodity = getTypeCommodity(quoteData) || []
  dataEdit.value.weightCommodity = quoteData.weightCommodity || null
  minWeightCommodity.value = quoteData.weightCommodity || -1
  priceOfflineFee.value = quoteData.priceOfflineFee || []
  dataWorkflow.value.positions = quoteData.positions || []
  dataEdit.value.tasks = [
    {
      code: '',
      value: null
    }
  ]
}

const quoteRequestChange = (values: any, setTaskEmpty: boolean = true) => {
  const quoteRequestData = values?.[0] || ({} as any)
  dataWorkflow.value.positions = quoteRequestData.positions || []
  if (setTaskEmpty) {
    dataEdit.value.positions = quoteRequestData.positions || []
    dataEdit.value.transportation = quoteRequestData.transportation || ''
    dataEdit.value.mergeContainer = quoteRequestData.mergeContainer || false
    dataEdit.value.headContainer = quoteRequestData.headContainer || false
    dataEdit.value.vehicleCategory = quoteRequestData.vehicleCategory || ''
    dataEdit.value.vehicleCategoryName = quoteRequestData.vehicleCategoryName || ''
    dataEdit.value.contCategory = quoteRequestData.contCategory || null
    dataEdit.value.typeCommodity = getTypeCommodity(quoteRequestData) || []
    dataEdit.value.weightCommodity = quoteRequestData.weightCommodity || null
    minWeightCommodity.value = quoteRequestData.weightCommodity || -1
    dataEdit.value.workflow = quoteRequestData.workflow || ''
    dataEdit.value.workflowName = quoteRequestData.workflowName || ''
    dataEdit.value.tasks = [
      {
        code: '',
        value: null
      }
    ]
  }
}

const disabled = computed(() => {
  if (dataEdit.value.createPriceFrom === 3) {
    return false
  }

  return true
})

const closeOverSide = () => {
  visible.value = false
}

const customerChange = (val: any) => {
  dataEdit.value.contact = null
  dataEdit.value.contactName = ''
  dataEdit.value.email = ''
  dataEdit.value.phone = ''
}

const listLabelCont: { [key: number]: any } = {
  10: $t('labels.vehicleModule.10ft'),
  20: $t('labels.vehicleModule.20ft'),
  40: $t('labels.vehicleModule.40ft'),
  45: $t('labels.vehicleModule.45ft'),
  50: $t('labels.vehicleModule.other')
}

defineExpose({ open })
</script>
